import React, { useEffect, useState } from "react";
import api from "../api";
import "../Styles/PurchaseJournal.css";
import { useNavigate } from "react-router-dom";

import {
  ShoppingCart,
  ArrowLeft,
  CalendarDays,
  Wallet,
  Landmark,
  ChevronRight,
} from "lucide-react";

const CASH_ACCOUNT_IDS = [13, 14];
const PURCHASE_ACCOUNT_IDS = [23, 22, 17, 15, 12, 10, 9, 8];

const PurchaseJournal = () => {

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const now = new Date();

  const [selectedMonth, setSelectedMonth] = useState(
    now.getMonth()
  );

  const [selectedYear, setSelectedYear] = useState(
    now.getFullYear()
  );

  useEffect(() => {

    const loadJournal = async () => {

      try {

        setLoading(true);

        const allTxns = [];

        const accounts = await api.getAccounts();

        for (const acc of accounts) {

          const txns = await api.getTransactions(acc.id);

          for (const txn of txns) {

            allTxns.push({
              ...txn,
              accountId: txn.account_id,
              accountName: acc.name,
            });

          }

        }

        const grouped = {};

        for (const txn of allTxns) {

          if (!txn.reference_id) continue;

          if (!grouped[txn.reference_id]) {
            grouped[txn.reference_id] = [];
          }

          grouped[txn.reference_id].push(txn);

        }

        const journalEntries = [];

        for (const [refId, txns] of Object.entries(grouped)) {

          const debitTxn = txns.find(
            (t) =>
              PURCHASE_ACCOUNT_IDS.includes(
                Number(t.accountId)
              ) &&
              parseFloat(t.debit) > 0
          );

          const creditTxn = txns.find(
            (t) =>
              (
                CASH_ACCOUNT_IDS.includes(
                  Number(t.accountId)
                ) ||
                Number(t.accountId) === 24
              ) &&
              parseFloat(t.credit) > 0
          );

          if (debitTxn && creditTxn) {

            const txnDate = new Date(debitTxn.date);

            const txnMonth = txnDate.getMonth();

            const txnYear = txnDate.getFullYear();

            if (
              txnMonth === selectedMonth &&
              txnYear === selectedYear
            ) {

              journalEntries.push({
                date: debitTxn.date,
                particulars: debitTxn.details,
                amount: parseFloat(debitTxn.debit),
                paidVia:
                  Number(creditTxn.accountId) === 24
                    ? "Opening Adjustment"
                    : creditTxn.accountName,
              });

            }

          }

          /* FOREX */

          const forexDebitTxn = txns.find(
            (t) =>
              [26, 27].includes(
                Number(t.accountId)
              ) &&
              parseFloat(t.debit) > 0
          );

          const forexCreditTxn = txns.find(
            (t) =>
              (
                CASH_ACCOUNT_IDS.includes(
                  Number(t.accountId)
                ) ||
                Number(t.accountId) === 24
              ) &&
              parseFloat(t.credit) > 0
          );

          if (
            forexDebitTxn &&
            forexCreditTxn
          ) {

            const txnDate = new Date(
              forexDebitTxn.date
            );

            const txnMonth =
              txnDate.getMonth();

            const txnYear =
              txnDate.getFullYear();

            if (
              txnMonth === selectedMonth &&
              txnYear === selectedYear
            ) {

              journalEntries.push({
                date: forexDebitTxn.date,

                particulars:
                  `Purchase of ${
                    forexDebitTxn.accountId === 26
                      ? "USD"
                      : "CAN"
                  } - ${forexDebitTxn.details}`,

                amount: parseFloat(
                  forexCreditTxn.credit
                ),

                paidVia:
                  Number(
                    forexCreditTxn.accountId
                  ) === 24
                    ? "Opening Adjustment"
                    : forexCreditTxn.accountName,
              });

            }

          }

        }

        journalEntries.sort(
          (a, b) =>
            new Date(a.date) -
            new Date(b.date)
        );

        setEntries(journalEntries);

      } catch (err) {

        console.error(
          "❌ Failed to load purchase journal:",
          err
        );

      } finally {

        setLoading(false);

      }

    };

    loadJournal();

  }, [selectedMonth, selectedYear]);

  const total = entries.reduce(
    (sum, e) => sum + e.amount,
    0
  );

  if (loading) {

    return (
      <div className="purchase-page">

        <div className="purchase-loading-card">

          <div className="purchase-spinner"></div>

          <p>
            Loading Purchase Journal...
          </p>

        </div>

      </div>
    );

  }

  return (

    <div className="purchase-page">

      {/* =====================================
          HERO
      ===================================== */}

      <div className="purchase-hero">

        <div className="purchase-hero-left">

          <div className="purchase-pill">
            <ShoppingCart size={15} />
            <span>Purchase Journal</span>
          </div>

          <h1>
            Purchase
            <br />
            Management
          </h1>

          <p>
            Track supplier purchases,
            inventory acquisitions,
            forex purchases and
            operational spending
            across your business ecosystem.
          </p>

          <div className="purchase-actions">

            <button
              className="purchase-btn primary"
              onClick={() =>
                navigate("/journals")
              }
            >
              Open Journals
              <ChevronRight size={17} />
            </button>

            <button
              className="purchase-btn secondary"
              onClick={() =>
                navigate("/accounts")
              }
            >
              <ArrowLeft size={16} />
              Accounts
            </button>

          </div>

        </div>

        {/* RIGHT SIDE */}

        <div className="purchase-summary-grid">

          <div className="summary-card green">

            <div className="summary-icon">
              <Wallet size={28} />
            </div>

            <div>
              <span>Total Purchases</span>
              <h2>
                $
                {total.toLocaleString()}
              </h2>
            </div>

          </div>

          <div className="summary-card blue">

            <div className="summary-icon">
              <CalendarDays size={28} />
            </div>

            <div>
              <span>Transactions</span>
              <h2>
                {entries.length}
              </h2>
            </div>

          </div>

          <div className="summary-card orange">

            <div className="summary-icon">
              <Landmark size={28} />
            </div>

            <div>
              <span>Selected Period</span>
              <h2>
                {new Date(
                  selectedYear,
                  selectedMonth
                ).toLocaleString(
                  "en-US",
                  {
                    month: "long",
                    year: "numeric",
                  }
                )}
              </h2>
            </div>

          </div>

        </div>

      </div>

      {/* =====================================
          FILTER BAR
      ===================================== */}

      <div className="purchase-filter-bar">

        <div className="filter-group">

          <label>Month</label>

          <select
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 12 },
              (_, i) => (

                <option
                  key={i}
                  value={i}
                >
                  {new Date(
                    0,
                    i
                  ).toLocaleString(
                    "en-US",
                    {
                      month: "long",
                    }
                  )}
                </option>

              )
            )}

          </select>

        </div>

        <div className="filter-group">

          <label>Year</label>

          <select
            value={selectedYear}
            onChange={(e) =>
              setSelectedYear(
                parseInt(e.target.value)
              )
            }
          >

            {Array.from(
              { length: 5 },
              (_, i) => {

                const year =
                  now.getFullYear() - 2 + i;

                return (
                  <option
                    key={year}
                    value={year}
                  >
                    {year}
                  </option>
                );

              }
            )}

          </select>

        </div>

      </div>

      {/* =====================================
          TABLE
      ===================================== */}

      <div className="purchase-table-wrapper">

        <table className="purchase-table">

          <thead>

            <tr>
              <th>Date</th>
              <th>Particulars</th>
              <th>Paid From</th>
              <th>Amount</th>
            </tr>

          </thead>

          <tbody>

            {entries.map((e, i) => (

              <tr key={i}>

                <td>
                  {e.date.split("T")[0]}
                </td>

                <td>
                  {e.particulars}
                </td>

                <td>
                  {e.paidVia}
                </td>

                <td className="purchase-amount">
                  $
                  {e.amount.toLocaleString()}
                </td>

              </tr>

            ))}

            <tr className="total-row">

              <td colSpan="3">
                Total Purchases
              </td>

              <td>
                $
                {total.toLocaleString()}
              </td>

            </tr>

          </tbody>

        </table>

      </div>

    </div>

  );

};

export default PurchaseJournal;